import React from 'react';
import {
  AlignHorizontalJustifyStart,
  AlignHorizontalJustifyCenter,
  AlignHorizontalJustifyEnd,
  AlignVerticalJustifyStart,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  AlignHorizontalDistributeCenter,
  AlignVerticalDistributeCenter,
  LucideIcon
} from 'lucide-react';
import { LabelElement } from '../../../../../types/labelDesign';
import { useTranslation } from '../../../../../contexts/I18nProvider';

export type AlignOp = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom';
export type DistributeOp = 'hdist' | 'vdist';

interface AlignToolbarProps {
  /** Elementene som er valgt (0, 1 eller flere). */
  selected: LabelElement[];
  /** Etikettens dimensjoner — brukes når kun ETT element justeres (mot etikettkant). */
  widthMm: number;
  heightMm: number;
  /** Kalles med kun de elementene som faktisk endret posisjon. */
  onApply: (updated: LabelElement[]) => void;
}

const round = (n: number) => Math.round(n * 100) / 100;

/**
 * Justerer valgte elementer. Med ETT element justeres det mot etikettkanten;
 * med FLERE justeres de mot hverandres samlede yttergrense (bounding box).
 * Endrer kun x/y — aldri bredde/høyde.
 */
function applyAlign(
  selected: LabelElement[],
  op: AlignOp,
  widthMm: number,
  heightMm: number
): LabelElement[] {
  if (selected.length === 0) return [];

  if (selected.length === 1) {
    const el = selected[0];
    switch (op) {
      case 'left': return [{ ...el, x: 0 }];
      case 'hcenter': return [{ ...el, x: round((widthMm - el.width) / 2) }];
      case 'right': return [{ ...el, x: round(widthMm - el.width) }];
      case 'top': return [{ ...el, y: 0 }];
      case 'vcenter': return [{ ...el, y: round((heightMm - el.height) / 2) }];
      case 'bottom': return [{ ...el, y: round(heightMm - el.height) }];
    }
  }

  const minLeft = Math.min(...selected.map((e) => e.x));
  const maxRight = Math.max(...selected.map((e) => e.x + e.width));
  const minTop = Math.min(...selected.map((e) => e.y));
  const maxBottom = Math.max(...selected.map((e) => e.y + e.height));
  const cx = (minLeft + maxRight) / 2;
  const cy = (minTop + maxBottom) / 2;

  return selected.map((el) => {
    switch (op) {
      case 'left': return { ...el, x: round(minLeft) };
      case 'hcenter': return { ...el, x: round(cx - el.width / 2) };
      case 'right': return { ...el, x: round(maxRight - el.width) };
      case 'top': return { ...el, y: round(minTop) };
      case 'vcenter': return { ...el, y: round(cy - el.height / 2) };
      case 'bottom': return { ...el, y: round(maxBottom - el.height) };
      default: return el;
    }
  });
}

/**
 * Fordeler ≥3 elementer jevnt langs en akse ved å holde første/siste senter fast
 * og spre de mellomliggende sentrene med lik avstand.
 */
function applyDistribute(selected: LabelElement[], op: DistributeOp): LabelElement[] {
  if (selected.length < 3) return [];

  const axis = op === 'hdist' ? 'x' : 'y';
  const sizeKey = op === 'hdist' ? 'width' : 'height';
  const center = (e: LabelElement) => e[axis] + e[sizeKey] / 2;

  const sorted = [...selected].sort((a, b) => center(a) - center(b));
  const firstC = center(sorted[0]);
  const lastC = center(sorted[sorted.length - 1]);
  const step = (lastC - firstC) / (sorted.length - 1);

  return sorted.map((el, i) => {
    if (i === 0 || i === sorted.length - 1) return el; // endepunkter fast
    const targetCenter = firstC + i * step;
    const newPos = round(targetCenter - el[sizeKey] / 2);
    return { ...el, [axis]: newPos };
  });
}

const Btn: React.FC<{ title: string; Icon: LucideIcon; disabled: boolean; onClick: () => void }> = ({
  title,
  Icon,
  disabled,
  onClick
}) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={onClick}
    className="p-1.5 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)] disabled:opacity-30 disabled:cursor-not-allowed"
  >
    <Icon size={16} />
  </button>
);

export const AlignToolbar: React.FC<AlignToolbarProps> = ({ selected, widthMm, heightMm, onApply }) => {
  const { t } = useTranslation();
  const canAlign = selected.length >= 1;
  const canDistribute = selected.length >= 3;

  const doAlign = (op: AlignOp) => onApply(applyAlign(selected, op, widthMm, heightMm));
  const doDistribute = (op: DistributeOp) => onApply(applyDistribute(selected, op));

  const hint =
    selected.length === 0
      ? t('manager.modules.labelDesigner.align.hintNone', {}, 'Velg element(er) for å justere')
      : selected.length === 1
      ? t('manager.modules.labelDesigner.align.hintEdge', {}, 'Justerer mot etikettkant')
      : t('manager.modules.labelDesigner.align.hintMulti', { '0': selected.length }, `Justerer ${selected.length} elementer mot hverandre`);

  return (
    <div className="flex items-center gap-1" title={hint}>
      <Btn title={t('manager.modules.labelDesigner.align.left', {}, 'Venstrejuster')} Icon={AlignHorizontalJustifyStart} disabled={!canAlign} onClick={() => doAlign('left')} />
      <Btn title={t('manager.modules.labelDesigner.align.hcenter', {}, 'Sentrer horisontalt')} Icon={AlignHorizontalJustifyCenter} disabled={!canAlign} onClick={() => doAlign('hcenter')} />
      <Btn title={t('manager.modules.labelDesigner.align.right', {}, 'Høyrejuster')} Icon={AlignHorizontalJustifyEnd} disabled={!canAlign} onClick={() => doAlign('right')} />
      <span className="w-px h-5 bg-[var(--color-border)] mx-0.5" />
      <Btn title={t('manager.modules.labelDesigner.align.top', {}, 'Toppjuster')} Icon={AlignVerticalJustifyStart} disabled={!canAlign} onClick={() => doAlign('top')} />
      <Btn title={t('manager.modules.labelDesigner.align.vcenter', {}, 'Sentrer vertikalt')} Icon={AlignVerticalJustifyCenter} disabled={!canAlign} onClick={() => doAlign('vcenter')} />
      <Btn title={t('manager.modules.labelDesigner.align.bottom', {}, 'Bunnjuster')} Icon={AlignVerticalJustifyEnd} disabled={!canAlign} onClick={() => doAlign('bottom')} />
      <span className="w-px h-5 bg-[var(--color-border)] mx-0.5" />
      <Btn title={t('manager.modules.labelDesigner.align.distH', {}, 'Fordel jevnt horisontalt (≥3)')} Icon={AlignHorizontalDistributeCenter} disabled={!canDistribute} onClick={() => doDistribute('hdist')} />
      <Btn title={t('manager.modules.labelDesigner.align.distV', {}, 'Fordel jevnt vertikalt (≥3)')} Icon={AlignVerticalDistributeCenter} disabled={!canDistribute} onClick={() => doDistribute('vdist')} />
    </div>
  );
};
